import { ColDef } from 'ag-grid-community';
import { useTranslateProductType } from 'hooks/use-translate-payoff';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import PriceButton from 'routes/smart-quote/components/prompt-results/components/price-button/price-button';
import PriceColumnHeader from 'routes/smart-quote/components/prompt-results/components/price-column-header';
import UnderlyingBadge from 'routes/smart-quote/components/prompt-results/underlying-badge';
import { SmartQuoteParsingResultWithRowNumber } from 'routes/smart-quote/hooks/use-group-prompt-results';
import { TenorDetails } from 'state/quotes/details/details.model';

const baseColumnDefinition: ColDef<SmartQuoteParsingResultWithRowNumber> = {
  suppressHeaderMenuButton: true,
  suppressHeaderContextMenu: true,
  sortable: false,
  resizable: false,
  contextMenuItems: ['copy'],
  headerClass: 'px-3 py-2',
  cellClass: 'px-3 py-2',
  cellStyle: { lineHeight: 1.2 },
};

const formatTenorValue = (value?: TenorDetails) => {
  if (!value) {
    return '';
  }
  const { unit, value: tenorValue } = value;
  return `${tenorValue} ${unit}${tenorValue > 1 ? 's' : ''}`;
};

const useColumnDefinition = (solveMode: string) => {
  const { formatMessage } = useIntl();
  const translateProductType = useTranslateProductType();

  const columnsDefinition = useMemo(
    (): ColDef<SmartQuoteParsingResultWithRowNumber>[] => [
      {
        ...baseColumnDefinition,
        field: 'rowNumber',
        headerName: '#',
        maxWidth: 48,
      },
      {
        ...baseColumnDefinition,
        field: 'productKey.instrument.contractName',
        headerName: formatMessage({ id: 'smartQuote.workspace.main.solveModeResults.column.productSubtype' }),
        valueGetter: ({ data }) =>
          data?.productKey?.instrument ? translateProductType(data.productKey.instrument.contractName) : '',
        tooltipValueGetter: ({ data }) =>
          data?.productKey?.instrument ? translateProductType(data.productKey.instrument.contractName) : '',
        flex: 1,
        cellClass: 'px-0 py-2 text-wrap',
        headerClass: 'px-0 py-2',
        autoHeight: true,
      },
      {
        ...baseColumnDefinition,
        field: 'underlyings',
        headerName: formatMessage({ id: 'smartQuote.workspace.main.solveModeResults.column.underlyings' }),
        cellRenderer: UnderlyingBadge,
        maxWidth: 244,
        autoHeight: true,
      },
      {
        ...baseColumnDefinition,
        field: 'currency',
        width: 46,
        headerName: formatMessage({ id: 'smartQuote.workspace.main.solveModeResults.column.currency' }),
        headerClass: 'py-2 px-0',
        cellClass: 'py-2 px-0',
      },
      {
        ...baseColumnDefinition,
        field: 'tenor',
        headerName: formatMessage({ id: 'smartQuote.workspace.main.solveModeResults.column.tenor' }),
        valueFormatter: ({ node }) => formatTenorValue(node?.data?.tenor),
        tooltipValueGetter: ({ node }) => formatTenorValue(node?.data?.tenor),
        maxWidth: 84,
        cellClass: 'px-3 py-2 text-wrap',
        autoHeight: true,
      },
      {
        ...baseColumnDefinition,
        field: 'externalQuoteId',
        headerName: formatMessage({ id: 'smartQuote.workspace.main.solveModeResults.column.externalQuoteId' }),
        maxWidth: 115,
        tooltipField: 'externalQuoteId',
      },
      {
        ...baseColumnDefinition,
        headerComponent: PriceColumnHeader,
        headerComponentParams: { solveMode },
        cellRenderer: PriceButton,
        pinned: 'right',
        cellClass: 'price-button',
        maxWidth: 128,
        headerClass: 'px-3',
      },
    ],
    [formatMessage, translateProductType, solveMode],
  );

  return columnsDefinition;
};

export default useColumnDefinition;
